import { useState, useEffect } from 'react';
import { getCurrentProfilePictureUrl } from '../utils/profilePictureUtils';

/**
 * Hook to fetch and cache current profile pictures for forum posts/replies
 * This ensures we always show the user's current profile picture, not the cached one from when the post was created
 */
const useCurrentProfilePicture = (userId, fallbackUrl = null) => {
  const [currentProfilePicture, setCurrentProfilePicture] = useState(fallbackUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      setCurrentProfilePicture(fallbackUrl);
      return;
    }

    let isMounted = true;

    const fetchCurrentProfilePicture = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const currentUrl = await getCurrentProfilePictureUrl(userId);
        
        if (isMounted) {
          // Use current profile picture if available, otherwise fall back to the cached one
          setCurrentProfilePicture(currentUrl || fallbackUrl);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching current profile picture:', err);
          setError(err.message);
          setCurrentProfilePicture(fallbackUrl); // Fall back to cached URL on error
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentProfilePicture();

    return () => {
      isMounted = false;
    };
  }, [userId, fallbackUrl]);

  return {
    currentProfilePicture,
    isLoading,
    error
  };
};

export default useCurrentProfilePicture;
